import React, { useRef } from 'react';
import HeroReveal from './HeroReveal';
import ProfileSnapshot from './ProfileSnapshot';
import TopMatches from './TopMatches';
import AllPathways from './AllPathways';
import ActionPlan from './ActionPlan';
import DownloadReport from './DownloadReport';
import Footer from '../landing/Footer';
import StickyCtaBar from '../landing/StickyCtaBar';

const FIT_PRIORITY = ['likely_fit', 'possible_fit', 'conditional_fit', 'insufficient_data', 'unlikely'];
const FIT_LABELS = {
  likely_fit: 'Likely Fit',
  possible_fit: 'Possible Fit',
  conditional_fit: 'Conditional',
  insufficient_data: 'Needs Data',
  unlikely: 'Unlikely',
};

const getUniqueCountries = (assessment) => {
  const seen = new Set();
  const all = [
    ...(assessment.pr_countries || []),
    ...(assessment.wp_countries || []),
    ...(assessment.conditional_opportunities || []),
  ];
  all.forEach(c => seen.add(c.country));
  return seen.size;
};

const getTopMatch = (assessment) => {
  const all = [
    ...(assessment.pr_countries || []).map(c => ({ ...c, fit: c.pr_fit })),
    ...(assessment.wp_countries || []).map(c => ({ ...c, fit: c.wp_fit })),
  ];
  let best = all[0];
  for (const c of all) {
    if (FIT_PRIORITY.indexOf(c.fit) < FIT_PRIORITY.indexOf(best?.fit || 'unlikely')) {
      best = c;
    }
  }
  return best || { country: 'Canada', fit: 'possible_fit' };
};

const ResultsExperience = ({ assessment, userData }) => {
  const contentRef = useRef(null);

  const recommendations = assessment.recommendations || [];
  const totalCountries = recommendations.length > 0
    ? recommendations.reduce((n, r) => n + Math.max(1, r.members?.length || 0), 0)
    : getUniqueCountries(assessment);

  let topCountry, topFitLabel;
  if (recommendations.length > 0) {
    const RANK = { strong_fit: 0, possible_fit: 1, business_review: 2, conditional_fit: 2, insufficient_data: 3 };
    const best = [...recommendations].sort((a, b) => (RANK[a.fit_key] ?? 4) - (RANK[b.fit_key] ?? 4))[0];
    topCountry = best.group;
    topFitLabel = best.display_label;
  } else {
    const topMatch = getTopMatch(assessment);
    topCountry = topMatch.country;
    topFitLabel = FIT_LABELS[topMatch.fit] || 'Possible Fit';
  }

  const summary = assessment.profile_summary || {};
  const nextActions = assessment.next_best_actions || [];

  const scrollToContent = () => {
    contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div data-testid="results-experience" style={{ background: 'var(--bg-primary)' }}>
      <div id="results-capture">
        {/* Section 1: Hero Reveal */}
        <HeroReveal
          userName={userData?.full_name}
          totalCountries={totalCountries}
          topCountry={topCountry}
          topFitLabel={topFitLabel}
          onScrollDown={scrollToContent}
        />

        {/* Section 2: Profile Snapshot */}
        <div ref={contentRef}>
          <ProfileSnapshot summary={summary} />
        </div>

        {/* Section 3: Top Matches */}
        <TopMatches assessment={assessment} />

        {/* Section 4: All Pathways */}
        <AllPathways assessment={assessment} />

        {/* Section 5: Action Plan */}
        <ActionPlan actions={nextActions} />
      </div>

      {/* Section 6: Download / Email Report (with consent) */}
      <DownloadReport
        userName={userData?.full_name}
        assessmentId={assessment.assessment_id}
        userId={assessment.user_id}
        initialConsent={!!userData?.contact_consent}
      />

      <Footer />
      <StickyCtaBar
        label="Download My Report"
        sublabel="Save your full assessment as a PDF — free"
        showAfter={900}
        onClick={() => {
          const el = document.querySelector('[data-testid="download-report-section"]');
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }}
      />
    </div>
  );
};

export default ResultsExperience;
